import { html } from 'lit';
import { Routes } from '@lit-labs/router';
import {
  AccountsController,
  status_options,
  feature_options,
  stripe_options
} from '../../lib/controllers/accounts.js';
import { request } from '../../lib/request.js';
import { throttle } from '../../lib/throttle.js';
import { pushState } from '../../lib/route.js';
import dropdown from '../../html/dropdown.js';
import { BaseElement } from '../base.js';
import './list.js';
import './account-details.js';
import './new-account-modal.js';

export class Accounts extends BaseElement {
  static properties = {
    account: { reflect: true },
    account_type: { attribute: 'account-type' },
    flushing: { state: true },
    query: { state: true },
    order_by: { state: true }
  };

  constructor() {
    super();
    this.routes = new Routes(this, [
      {
        path: ':id/*',
        render: ({ id }) => this.renderAccountDetails(id)
      },
      {
        path: '*',
        render: () => this.renderAccountsList()
      }
    ]);
    this.accounts = null;
    this.flushing = false;
    this.status = null;
    this.feature = null;
    this.stripe = null;
    this.account_type = 'main';
    this.query = '';
    this.throttledQuery = throttle((event) =>
      this.setQuery(event.target.value)
    );
  }

  render() {
    return this.routes.outlet();
  }

  setQuery(query) {
    this.query = query;
    this.ensureAccounts();
    this.accounts?.setSearch(query);
  }

  setStatus(status) {
    this.status = status;
    this.ensureAccounts();
    this.accounts?.setStatus(status);
  }

  setFeature(feature) {
    this.feature = feature;
    this.ensureAccounts();
    this.accounts?.setFeature(feature);
  }

  setStripe(stripe) {
    this.stripe = stripe;
    this.ensureAccounts();
    this.accounts?.setStripe(stripe);
  }

  setPage(page_index) {
    this.ensureAccounts();
    this.accounts?.setPage(page_index);
  }

  onAddAccount() {
    this.emit(
      'modal:open',
      html`
        <modal-new-account
          @added=${({ detail }) => this.addAccount(detail)}
        ></modal-new-account>
      `
    );
  }

  onFlush() {
    this.emit('modal:confirm', {
      description: 'Do you really want to flush the accounts cache?',
      confirm: async () => {
        this.flushing = true;
        try {
          await request('DELETE', '/accounts');
        } finally {
          this.flushing = false;
        }
      }
    });
  }

  addAccount(detail) {
    pushState(this.routes, `${detail.account}/locations`);
  }

  ensureAccounts() {
    if (!this.accounts) {
      const account_type = this.account_type === 'staff' ? 'staff' : 'main';
      this.accounts = new AccountsController(this, account_type);
      this.accounts?.task.run();
    }
  }

  renderAccountsList() {
    this.ensureAccounts();
    return html`
      <div class="hstack">
        <h1 class="me-auto d-flex align-items-center gap-2">
          <i
            class="bi bi-${this.account_type === 'staff'
              ? 'person'
              : 'building'}"
          ></i>
          ${this.account_type === 'staff' ? 'Staff Accounts' : 'Main Accounts'}
        </h1>
        <button
          type="button"
          class="btn btn-sm btn-outline-warning"
          ?disabled=${this.flushing}
          @click=${this.onFlush}
        >
          Flush
        </button>
      </div>
      <shared-search-bar
        id="search-bar"
        @input=${this.throttledQuery}
        value=${this.query}
      >
        ${dropdown({
          icon: 'activity',
          options: status_options,
          selected: this.status,
          onSelect: (value) => this.setStatus(value)
        })}
        ${this.account_type === 'main'
          ? dropdown({
              icon: 'flag',
              options: feature_options,
              selected: this.feature,
              onSelect: (value) => this.setFeature(value)
            })
          : null}
        ${this.account_type === 'main'
          ? dropdown({
              icon: 'arrow-repeat',
              options: stripe_options,
              selected: this.stripe,
              onSelect: (value) => this.setStripe(value)
            })
          : null}
      </shared-search-bar>
      ${this.renderAccountsListContent()}
    `;
  }

  renderAccountsListContent() {
    return this.accounts?.render(({ accounts }) => {
      const { page_index, page_count } = this.accounts?.pagination || {};
      return html`
        <accounts-list
          .items=${accounts}
          .show_migration=${this.account_type === 'main'}
          .show_roles=${this.account_type === 'staff'}
          base-path=${this.account_type === 'staff'
            ? '/staff-accounts/'
            : '/accounts/'}
        ></accounts-list>
        <shared-actions-bar>
          ${this.account_type === 'main'
            ? html`
                <button
                  class="btn btn-sm btn-primary"
                  @click=${() => this.onAddAccount()}
                >
                  Create New
                </button>
              `
            : null}
          <shared-pagination
            class="ms-auto"
            page-count=${page_count}
            page-index=${page_index}
            @pagination:pageIndex=${(event) => this.setPage(event.detail)}
          ></shared-pagination>
        </shared-actions-bar>
      `;
    });
  }

  renderAccountDetails(id) {
    return html`
      <account-details
        account=${id}
        base-path=${this.account_type === 'staff'
          ? '/staff-accounts/'
          : '/accounts/'}
      ></account-details>
    `;
  }
}

customElements.define('app-accounts', Accounts);
